/**
 * Shared mapping between the CCSD Cost Changes Report filter forms and the API query parameters.
 *
 * The two tabs reach the CCSD code through different associations, and API Platform ANDs filter
 * properties, so there is no single shared parameter — each tab passes its own code filter key.
 */

export const CONSULTANT_CODE_FILTER = 'ccsdCodeCost.ccsdCode.reference';
export const FEE_ASSURED_CODE_FILTER = 'defaultCcsdCodeCost.ccsdCode.reference';
export const FEE_FROM_FILTER = 'cost[gte]';
export const FEE_TO_FILTER = 'cost[lte]';

const feeBound = (value) => {
  if (value === null || value === undefined || String(value).trim() === '') {
    return null;
  }

  const bound = Number(value);

  return Number.isNaN(bound) ? null : bound;
};

const asFieldValue = (value) => (value === null || value === undefined ? '' : value);

/**
 * Filters are serialised by parseFilterObjectToQueryString with encode: false, so anything a user
 * types goes into the query string verbatim. CCSD references are alphanumeric, so drop everything
 * else rather than let a stray & or = split the parameter.
 */
const searchTerm = (value) => (typeof value === 'string' ? value.replace(/[^\w.-]/g, '') : '');

/**
 * Rewrites the plain form values into API parameters, leaving every other filter alone.
 *
 * A cleared filter is set to null rather than removed: the containers drop null keys when they
 * merge the default, active and session filters, so null is how a filter is switched off.
 */
export const applyCodeAndFeeFilters = (values, codeFilterKey) => {
  const filters = { ...values };

  const code = searchTerm(filters.code);
  filters[codeFilterKey] = code === '' ? null : code;
  delete filters.code;

  filters[FEE_FROM_FILTER] = feeBound(filters.feeFrom);
  filters[FEE_TO_FILTER] = feeBound(filters.feeTo);
  delete filters.feeFrom;
  delete filters.feeTo;

  return filters;
};

/**
 * Reads the stored session filters back into the form fields, so they survive a page refresh.
 */
export const codeAndFeeInitialValues = (session, codeFilterKey) => {
  const stored = session || {};

  return {
    code: asFieldValue(stored[codeFilterKey]),
    feeFrom: asFieldValue(stored[FEE_FROM_FILTER]),
    feeTo: asFieldValue(stored[FEE_TO_FILTER]),
  };
};
